import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ROLE_LABELS } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UserPlus, KeyRound, Lock, Trash2, Search, ShieldOff, ShieldCheck, MailCheck,
} from "lucide-react";

const EMPTY_FORM = {
  name: "", email: "", role: "ministry_support",
  title: "", phone: "", department: "",
  initial_password: "",
};

function formatLastLogin(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
}

export default function UserManagementTab({
  users, me, onChangeRole, onToggleKpiEdit, onToggleNewsletterEditor, onReload,
}) {
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);

  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (!showInactive && u.active === false) return false;
      if (!q) return true;
      return [u.name, u.email, u.title, u.department].some(
        (v) => (v || "").toLowerCase().includes(q),
      );
    });
  }, [users, search, showInactive]);

  const submitCreate = async () => {
    const email = form.email.trim().toLowerCase();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!email || !email.includes("@")) return toast.error("Valid email is required");
    setCreating(true);
    try {
      const { data: created } = await api.post("/users", {
        name: form.name.trim(),
        email,
        role: form.role,
        title: form.title.trim() || null,
        phone: form.phone.trim() || null,
        department: form.department.trim() || null,
      });
      if (form.initial_password.trim()) {
        try {
          await api.post(`/users/${created.user_id}/set-password`, {
            password: form.initial_password.trim(),
          });
        } catch (e) {
          toast.warning("User created but password failed: " + (e?.response?.data?.detail || ""));
        }
      }
      toast.success(`Added ${created.name}`);
      setOpenCreate(false);
      setForm(EMPTY_FORM);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create user");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u, value) => {
    try {
      await api.patch(`/users/${u.user_id}`, { active: value });
      toast.success(value ? `${u.name} re-activated` : `${u.name} disabled`);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update");
    }
  };

  const submitPassword = async () => {
    if (!pwTarget) return;
    if (pwValue.length < 8) return toast.error("Password must be at least 8 characters");
    setPwSaving(true);
    try {
      await api.post(`/users/${pwTarget.user_id}/set-password`, { password: pwValue });
      toast.success(`Password set for ${pwTarget.name}`);
      setPwTarget(null);
      setPwValue("");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't set password");
    } finally {
      setPwSaving(false);
    }
  };

  const clearPassword = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/clear-password`);
      toast.success(`Password cleared for ${u.name}`);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't clear password");
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.user_id}`);
      toast.success(`${deleteTarget.name} removed`);
      setDeleteTarget(null);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove user");
    }
  };

  return (
    <div className="space-y-5" data-testid="user-management-tab">
      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden">
        <div className="px-6 py-5 border-b border-brand-primary/10 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading text-xl text-brand-primary">Staff & Roles</h3>
            <p className="text-sm text-brand-primary/65 mt-1">
              Add new staff, set passwords, manage roles. Google sign-in is restricted to emails listed here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-primary/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff…"
                className="h-9 pl-9 w-52"
                data-testid="user-search-input"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-brand-primary/65">
              <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="user-show-inactive-toggle" />
              Show inactive
            </label>
            <Dialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (!o) setForm(EMPTY_FORM); }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-9 px-4 shadow-warm" data-testid="add-user-button">
                  <UserPlus className="h-4 w-4 mr-1.5" /> Add user
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-user-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add a staff member</DialogTitle>
                  <DialogDescription>
                    They'll be able to sign in via Google (matching email) or with the password you set below.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="new-user-name">Name *</Label>
                      <Input id="new-user-name" data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jonathan Glover" />
                    </div>
                    <div>
                      <Label htmlFor="new-user-email">Email *</Label>
                      <Input id="new-user-email" data-testid="new-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="staff@familychapel.org" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="new-user-title">Title</Label>
                      <Input id="new-user-title" data-testid="new-user-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Worship Pastor" />
                    </div>
                    <div>
                      <Label htmlFor="new-user-department">Department</Label>
                      <Input id="new-user-department" data-testid="new-user-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Worship & Production" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="new-user-phone">Phone</Label>
                      <Input id="new-user-phone" data-testid="new-user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="555-0123" />
                    </div>
                    <div>
                      <Label htmlFor="new-user-role">Role</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger id="new-user-role" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(ROLE_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="new-user-pw">Initial password (optional)</Label>
                    <Input
                      id="new-user-pw"
                      type="password"
                      data-testid="new-user-password"
                      value={form.initial_password}
                      onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                      placeholder="Leave blank if user signs in via Google"
                    />
                    <p className="text-xs text-brand-primary/55 mt-1">
                      Min 8 characters. Required only for non-Google users.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
                  <Button onClick={submitCreate} disabled={creating} className="bg-terracotta hover:bg-terracotta-hover text-white" data-testid="new-user-submit">
                    {creating ? "Adding…" : "Add user"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]" data-testid="users-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-brand-primary/55 bg-cream/50">
                <th className="px-6 py-3 font-medium">Staff member</th>
                <th className="px-6 py-3 font-medium">Role</th>
                <th className="px-6 py-3 font-medium">Permissions</th>
                <th className="px-6 py-3 font-medium">Login</th>
                <th className="px-6 py-3 font-medium">Last login</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-primary/10">
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-brand-primary/55">
                  No staff matched.
                </td></tr>
              )}
              {filtered.map((u) => {
                const isSelf = u.user_id === me?.user_id;
                const inactive = u.active === false;
                return (
                  <tr key={u.user_id} className={`hover:bg-cream/40 ${inactive ? "opacity-60" : ""}`} data-testid={`user-row-${u.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-cream grid place-items-center overflow-hidden border border-brand-primary/10 shrink-0">
                          {u.picture ? <img src={u.picture} className="h-full w-full object-cover" alt="" /> :
                            <span className="font-heading text-sm text-terracotta">{u.name?.[0]?.toUpperCase()}</span>}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-brand-primary truncate">{u.name}</span>
                            {isSelf && <Badge className="rounded-full bg-sage/20 text-brand-primary border-0 text-[10px]">you</Badge>}
                            {inactive && <Badge className="rounded-full bg-terracotta/15 text-terracotta border-0 text-[10px]">inactive</Badge>}
                          </div>
                          <p className="text-xs text-brand-primary/60 truncate">{u.email}</p>
                          {u.title && <p className="text-xs text-brand-primary/45 truncate">{u.title}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Select value={u.role} onValueChange={(v) => onChangeRole(u.user_id, v)}>
                        <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-44 rounded-full bg-white border-brand-primary/15 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ROLE_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4 text-xs space-y-1">
                      {u.role === "ministry_support" ? (
                        <label className="flex items-center gap-2 text-brand-primary/70">
                          <Switch
                            data-testid={`kpi-edit-switch-${u.user_id}`}
                            checked={!!u.can_edit_kpi}
                            onCheckedChange={(v) => onToggleKpiEdit(u.user_id, v)}
                          />
                          KPI edit
                        </label>
                      ) : <p className="text-brand-primary/50">KPI edit ✓ (role default)</p>}
                      {u.role !== "admin" && (
                        <label className="flex items-center gap-2 text-brand-primary/70">
                          <Switch
                            data-testid={`newsletter-editor-switch-${u.user_id}`}
                            checked={!!u.newsletter_editor}
                            onCheckedChange={(v) => onToggleNewsletterEditor(u.user_id, v)}
                          />
                          Newsletter editor
                        </label>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <div className="space-y-0.5">
                        <p className="text-brand-primary/70 inline-flex items-center gap-1">
                          <MailCheck className="h-3 w-3" /> Google
                        </p>
                        <p className={`inline-flex items-center gap-1 ${u.has_password ? "text-sage" : "text-brand-primary/40"}`}>
                          <KeyRound className="h-3 w-3" />
                          {u.has_password ? "Password set" : "No password"}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs text-brand-primary/55 whitespace-nowrap">
                      {formatLastLogin(u.last_login_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 justify-end flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setPwTarget(u); setPwValue(""); }}
                          data-testid={`set-password-${u.user_id}`}
                          className="h-8 text-xs"
                          title="Set / reset password"
                        >
                          <KeyRound className="h-3.5 w-3.5 mr-1" /> {u.has_password ? "Reset" : "Set"} password
                        </Button>
                        {u.has_password && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => clearPassword(u)}
                            data-testid={`clear-password-${u.user_id}`}
                            className="h-8 text-xs text-brand-primary/65 hover:text-terracotta"
                            title="Remove password (Google login only)"
                          >
                            <Lock className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(u, !inactive ? false : true)}
                          disabled={isSelf}
                          data-testid={`toggle-active-${u.user_id}`}
                          className="h-8 text-xs"
                          title={isSelf ? "Can't disable yourself" : (inactive ? "Re-activate" : "Disable")}
                        >
                          {inactive ? <ShieldCheck className="h-3.5 w-3.5 text-sage" /> : <ShieldOff className="h-3.5 w-3.5 text-brand-primary/55" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(u)}
                          disabled={isSelf}
                          data-testid={`delete-user-${u.user_id}`}
                          className="h-8 text-brand-primary/55 hover:text-terracotta"
                          title={isSelf ? "Can't delete yourself" : "Remove user"}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Set/reset password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setPwValue(""); } }}>
        <DialogContent data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {pwTarget?.has_password ? "Reset password" : "Set password"}
            </DialogTitle>
            <DialogDescription>
              For <span className="text-brand-primary font-medium">{pwTarget?.name}</span>{" "}
              ({pwTarget?.email}). Setting a password will revoke any existing sessions.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="pw-input">New password (min 8 characters)</Label>
            <Input
              id="pw-input"
              type="password"
              autoFocus
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              placeholder="••••••••"
              data-testid="set-password-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !pwSaving) submitPassword(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwTarget(null)}>Cancel</Button>
            <Button onClick={submitPassword} disabled={pwSaving || pwValue.length < 8} className="bg-terracotta hover:bg-terracotta-hover text-white" data-testid="set-password-submit">
              {pwSaving ? "Saving…" : "Save password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              All of their sessions will be revoked and their account record deleted. Their previously
              created content (announcements, KPIs, receipts) will be preserved but anonymous.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDelete}
              data-testid="delete-user-confirm"
              className="bg-terracotta hover:bg-terracotta-hover"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
